import type React from 'react'
import { memo, useLayoutEffect, useRef } from 'react'
import { BLOCK_LABELS } from '../blocks/catalog'
import type { Block } from '../blocks/types'
import { Icon } from './icons'

// Selection chrome around one block (prototype edBlock L1644): hover dashed /
// selected solid outline, label tab, condition pill, and the selected
// toolbar (drag grip · up · down · duplicate · delete).

export type ChromeAction = 'up' | 'down' | 'dup' | 'del'

const cx = (...c: Array<string | false | null | undefined>) =>
  c.filter(Boolean).join(' ')

export const BlockChrome: React.FC<{
  block: Block
  selected: boolean
  dim: boolean
  condLabel: string | null
  canUp: boolean
  canDown: boolean
  onSelect: (id: string) => void
  onAction: (a: ChromeAction, id: string) => void
  onDragStart: (e: React.DragEvent, id: string) => void
  onDragEnd: () => void
  children: React.ReactNode
}> = ({
  block,
  selected,
  dim,
  condLabel,
  canUp,
  canDown,
  onSelect,
  onAction,
  onDragStart,
  onDragEnd,
  children,
}) => {
  const label = BLOCK_LABELS[block.type] ?? block.type
  const act = (a: ChromeAction) => (e: React.MouseEvent) => {
    e.stopPropagation()
    onAction(a, block.id)
  }
  const drag = (e: React.DragEvent) => {
    e.stopPropagation()
    const el = (e.currentTarget as HTMLElement).closest('.eb')
    if (el) e.dataTransfer.setDragImage(el, 20, 16)
    onDragStart(e, block.id)
  }
  return (
    <div
      className={cx(
        'eb',
        selected && 'sel',
        dim && 'eb-dim',
        condLabel && 'has-cond',
      )}
      data-id={block.id}
      onClick={(e) => {
        e.stopPropagation()
        onSelect(block.id)
      }}
    >
      <div
        className="eb-tag"
        draggable
        onDragStart={drag}
        onDragEnd={onDragEnd}
      >
        {label}
      </div>
      {condLabel ? (
        <div className="eb-cond">
          <Icon name="filter" />
          {condLabel}
        </div>
      ) : null}
      {selected ? (
        <div className="eb-tools">
          <button
            type="button"
            className="grip"
            draggable
            title="Drag to move"
            aria-label="Drag to move"
            onDragStart={drag}
            onDragEnd={onDragEnd}
            onClick={(e) => e.stopPropagation()}
          >
            <Icon name="grip" />
          </button>
          <button
            type="button"
            title="Move up"
            aria-label="Move up"
            disabled={!canUp}
            onClick={act('up')}
          >
            <Icon name="up" />
          </button>
          <button
            type="button"
            title="Move down"
            aria-label="Move down"
            disabled={!canDown}
            onClick={act('down')}
          >
            <Icon name="down" />
          </button>
          <button
            type="button"
            title="Duplicate"
            aria-label="Duplicate"
            onClick={act('dup')}
          >
            <Icon name="copy" />
          </button>
          <button
            type="button"
            className="danger"
            title="Delete"
            aria-label="Delete"
            onClick={act('del')}
          >
            <Icon name="trash" />
          </button>
        </div>
      ) : null}
      <div className="eb-box">{children}</div>
    </div>
  )
}

// One block's compiled canvas HTML. Text fields the compiler marks with
// data-bs-field / data-bs-cell become contentEditable in edit mode. While a
// field has focus (`frozen`) the HTML shown stays as it was when editing
// started, so React doesn't replace the DOM under the caret on each
// keystroke; the fresh compile is shown once focus leaves.
export const BlockContent = memo(function BlockContent({
  html,
  frozen,
  editable,
}: {
  html: string
  frozen: boolean
  editable: boolean
}) {
  const shown = useRef(html)
  if (!frozen) shown.current = html
  const ref = useRef<HTMLDivElement>(null)
  // innerHTML as we last left it (compiled HTML + editable attributes).
  const applied = useRef<string | null>(null)
  const current = shown.current
  useLayoutEffect(() => {
    const root = ref.current
    if (!root) return
    // Once editing ends, the DOM must show the compiled doc. If the user's
    // typing changed it but the fresh compile is the same string React last
    // rendered (e.g. the edit was undone), React won't touch the DOM — so
    // rewrite it ourselves.
    if (
      !frozen &&
      applied.current !== null &&
      root.innerHTML !== applied.current
    )
      root.innerHTML = current
    const fields = root.querySelectorAll<HTMLElement>(
      '[data-bs-field], [data-bs-cell]',
    )
    for (const el of Array.from(fields)) {
      if (editable) {
        el.setAttribute('contenteditable', 'true')
        el.setAttribute('spellcheck', 'true')
        if (el.hasAttribute('data-bs-field')) el.classList.add('eb-text')
      } else {
        el.removeAttribute('contenteditable')
        el.classList.remove('eb-text')
      }
    }
    if (!frozen) applied.current = root.innerHTML
  }, [current, editable, frozen])
  // Compiled by compileBlock: user text is sanitised / escaped there.
  return <div ref={ref} dangerouslySetInnerHTML={{ __html: current }} />
})
